import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface RevenuePoint {
  month: string;
  revenue: number;
}

export default function RevenueChart() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("6M");
  const [data, setData] = useState<RevenuePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        const { dashboardApi } = await import("@/services/api");
        const result = await dashboardApi.getStats();
        if (result?.revenueData && Array.isArray(result.revenueData)) {
          setData(result.revenueData);
        } else {
          setData([]);
        }
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRevenue();
  }, []);

  const displayData = period === "3M" ? data.slice(-3) : period === "1Y" ? data : data.slice(-6);
  const maxRevenue = Math.max(...displayData.map((d) => d.revenue), 1);
  const totalRevenue = displayData.reduce((a, b) => a + b.revenue, 0);
  const avgRevenue = totalRevenue / (displayData.length || 1);

  return (
    <div
      onClick={() => navigate("/reports?section=revenue")}
      className="bg-white rounded-xl border border-amber-100/60 p-4 md:p-5 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer group"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-semibold text-gray-900 text-base">Revenue Trend</h3>
          <p className="text-xs text-gray-500 mt-0.5">Monthly billing overview</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg p-1 self-start sm:self-auto" style={{ background: "#fef3c7" }}>
          {["3M", "6M", "1Y"].map((p) => (
            <button
              key={p}
              onClick={(e) => { e.stopPropagation(); setPeriod(p); }}
              className="px-3 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={
                period === p
                  ? { background: "#f59e0b", color: "#fff" }
                  : { color: "#92400e" }
              }
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="h-44 flex items-end gap-2 md:gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1" style={{ height: "160px" }}>
              <div className="w-full rounded-t-lg animate-pulse" style={{ height: `${30 + i * 10}%`, background: "#fef3c7" }} />
              <div className="h-3 w-6 rounded animate-pulse" style={{ background: "#fef3c7" }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-end gap-1.5 md:gap-2.5 h-44">
          {displayData.map((d, i) => {
            const height = Math.max((d.revenue / maxRevenue) * 100, 4);
            const isLast = i === displayData.length - 1;
            const isHovered = hoveredIdx === i;
            return (
              <div
                key={i}
                className="flex-1 flex flex-col items-center gap-1 group/chart cursor-pointer"
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={(e) => { e.stopPropagation(); navigate("/reports?section=revenue"); }}
              >
                <div className="relative w-full flex items-end justify-center" style={{ height: "160px" }}>
                  {/* Tooltip */}
                  {isHovered && (
                    <div className="absolute -top-9 left-1/2 -translate-x-1/2 text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap z-10 pointer-events-none"
                      style={{ background: "#1c1008" }}
                    >
                      ₹{(d.revenue / 100000).toFixed(1)}L
                    </div>
                  )}
                  <div
                    className="w-full rounded-t-lg transition-all duration-500"
                    style={{
                      height: `${height}%`,
                      background: isLast
                        ? "linear-gradient(180deg, #f59e0b 0%, #d97706 100%)"
                        : isHovered
                        ? "linear-gradient(180deg, #fcd34d 0%, #fbbf24 100%)"
                        : "linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)",
                      minHeight: "6px",
                      boxShadow: isLast ? "0 2px 8px rgba(245,158,11,0.3)" : "none",
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500">{d.month}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer stats */}
      <div className="mt-4 pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" style={{ borderTop: "1px solid #fef3c7" }}>
        <div>
          <p className="text-xs text-gray-500">Total ({period})</p>
          <p className="text-lg font-bold text-gray-900">₹{(totalRevenue / 100000).toFixed(1)}L</p>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-xs text-gray-500">Avg. Monthly</p>
          <p className="text-lg font-bold" style={{ color: "#d97706" }}>
            ₹{(avgRevenue / 100000).toFixed(1)}L
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm" style={{ background: "#f59e0b" }} />
          <span className="text-xs text-gray-500">Current Month</span>
        </div>
      </div>
    </div>
  );
}
